import CarouselTrackingService from 'JS/services/CarouselTrackingService';
import ProductService, { AddBundleToCartRequest, AddProductComparisonToCartRequest, AddToCartResult, IPDPAddToCartResponse, IWarrantyUpdateResponse } from "JS/services/ProductService";
import store from '../state/store';
import { getPriceMode, IProductJson } from 'JS/types/Product';
import { ICarouselClickGtmData } from '../../types/Carousel';
import { IProductDetailProductJson } from 'JS/types/PDP';
import { IQuickCartSummaryJson } from 'JS/types/Order';

export async function addToCart(product: IProductJson | IProductDetailProductJson, quantity: number, userConfirmed: boolean, userAgreed: boolean): Promise<IPDPAddToCartResponse> {
	const response = await store.dispatch('addToCart', { userConfirmed, userAgreed });
	
	// if cartSummary, update header with new cart data
	if (response.result == AddToCartResult.Success) {
		if (response.cartSummary) updateHeader(response.cartSummary);
		const cartProduct = response.cartSummary.productsInCart.find(
			cartProduct => cartProduct.blainNumber === product.blainNumber
		);
		store.dispatch('receiveUnitPriceFromCart', cartProduct.price.price);

		// Add to Cart event for Google Tag Manager
		const orderProducts = response.cartSummary.productsInCart.map(item => ({
			blainNumber: item.price.blainNumber,
			qty: item.qty,
			unitPrice: item.price.price,
			vendorBrand: item.vendorBrand,
			productName: item.productName,
			imageUrl: item.imageUrlLarger,
			productUrl: item.virtualURL
		}));

		window.blainGtmDataLayer.push({ orderProducts: null }); // orderProducts needs to be reset first to avoid conflicts
		window.blainGtmDataLayer.push({
			'blainNumber': product.blainNumber,
			'vendorBrand': cartProduct.vendorBrand,
			'productName': cartProduct.productName,
			'deliveryChannel': store.state.fulfillmentType,
			'totalValue': cartProduct.unitPrice,
			'qty': quantity,
			'orderProducts': orderProducts,
			'orderSubtotal': response.cartSummary.subtotal,
			'orderId': response.orderID,
			'event': "cart_ajax_add"
		});
	}

	return response;
}

export async function addBundleToCart(request: AddBundleToCartRequest): Promise<IPDPAddToCartResponse> {
	const response = await store.dispatch('addBundleToCart', request);

	// if cartSummary, update header with new cart data
	if (response.result == AddToCartResult.Success) {
		if (response.cartSummary) updateHeader(response.cartSummary);

		const cartProducts = response.cartSummary.productsInCart.filter(
			cartProduct => request.blainNumbers.includes(cartProduct.blainNumber)
		);
		
		const primaryProduct = cartProducts.find(
			cartProduct => cartProduct.blainNumber == request.primaryBlainNumber
		);

		if (!!primaryProduct) {
			store.dispatch('receiveUnitPriceFromCart', primaryProduct.price.price);
		}

		const orderProducts = response.cartSummary.productsInCart.map(item => ({
			blainNumber: item.price.blainNumber,
			qty: item.qty,
			unitPrice: item.price.price,
			vendorBrand: item.vendorBrand,
			productName: item.productName,
			imageUrl: item.imageUrlLarger,
			productUrl: item.virtualURL
		}));

		const trackingSvc = new CarouselTrackingService();

		cartProducts.forEach(product => {
			const gtmData: ICarouselClickGtmData = {
				itemListID: "bundle_upsell",
				itemListName: "PDP Bundle Upsell",
				index: 0,
				blainNumber: product.blainNumber,
				vendorBrand: product.vendorBrand,
				productName: product.productName,
				currentPrice: product.price.price,
				belowMap: product.price.isBelowMAP,
				priceMode: getPriceMode(product.price)
			};

			trackingSvc.trackClick(null, gtmData);

			window.blainGtmDataLayer.push({
				'blainNumber': product.blainNumber,
				'vendorBrand': product.vendorBrand,
				'productName': product.productName,
				'deliveryChannel': "Ship",
				'totalValue': product.unitPrice,
				'qty': 1,
				'orderProducts': orderProducts,
				'orderId': response.orderID,
				'event': "cart_ajax_add"
			});
		});
	}

	return response;
}

export async function addProductComparisonToCart(request: AddProductComparisonToCartRequest): Promise<IPDPAddToCartResponse> {
	const response = await store.dispatch('addProductComparisonToCart', request);

	// if cartSummary, update header with new cart data
	if (response.result == AddToCartResult.Success) {
		if (response.cartSummary) updateHeader(response.cartSummary);

		const cartProduct = response.cartSummary.productsInCart.find(
			cartProduct => cartProduct.blainNumber === request.blainNumber
		);
		store.dispatch('receiveUnitPriceFromCart', cartProduct.price.price);

		// Add to Cart event for Google Tag Manager
		const orderProducts = response.cartSummary.productsInCart.map(item => ({
			blainNumber: item.price.blainNumber,
			qty: item.qty,
			unitPrice: item.price.price,
			vendorBrand: item.vendorBrand,
			productName: item.productName,
			imageUrl: item.imageUrlLarger,
			productUrl: item.virtualURL
		}));

		window.blainGtmDataLayer.push({ orderProducts: null }); // orderProducts needs to be reset first to avoid conflicts
		window.blainGtmDataLayer.push({
			'blainNumber': request.blainNumber,
			'vendorBrand': cartProduct.vendorBrand,
			'productName': cartProduct.productName,
			'deliveryChannel': "Ship",
			'totalValue': cartProduct.unitPrice,
			'qty': 1,
			'orderProducts': orderProducts,
			'orderSubtotal': response.cartSummary.subtotal,
			'orderId': response.orderID,
			'event': "cart_ajax_add"
		});
	}

	return response;
}

export function updateHeader(cartSummary: IQuickCartSummaryJson ) {
	if (window.vueEventBus && cartSummary) {
		window.vueEventBus.$emit('update-quick-cart', cartSummary);
	}
}

export async function addWarranty(productBlainNumber: string, warrantyQty: number, warrantySku: string): Promise<IWarrantyUpdateResponse> {
	const productService = new ProductService();
	const response: IWarrantyUpdateResponse = await productService.setWarranty({
		warrantySku: warrantySku,
		warrantyQty: warrantyQty,
		productBlainNumber: productBlainNumber
	});

	if (response.success) {
		window.blainGtmDataLayer.push({
			'blainNumber': response.warranty.blainNumber,
			'qty': warrantyQty,
			'totalValue': response.warranty.price,
			'event': "warranty_added_pdp"
		});
	}

	return response;
}